import { Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { formatINR } from "@/lib/qgCalc";
import { ItemCard } from "./ItemCard";

const CHOICES = [
  ["package", "Charge the package / turnkey price", "Component rows are listed for reference only and not charged."],
  ["itemized", "Charge itemized components", "The package row is not charged; each component is priced."],
];

function PricingChoice({ group, onChange, readOnly, testId }) {
  return (
    <div className={`rounded-lg border p-3 ${group.pricing_choice ? "border-indigo-100 bg-indigo-50/50 dark:bg-indigo-950/20" : "border-red-200 bg-red-50/60 dark:bg-red-950/20"}`} data-testid={testId}>
      <p className="text-xs font-semibold text-slate-800 dark:text-slate-100 mb-2">This group has a package price that overlaps its components — choose how to price it so work is counted once.</p>
      <div className="grid md:grid-cols-2 gap-2">
        {CHOICES.map(([v, title, hint]) => (
          <button type="button" key={v} disabled={readOnly} onClick={() => onChange(v)} data-testid={`${testId}-${v}`}
            className={`text-left rounded-md border px-3 py-2 transition-colors ${group.pricing_choice === v ? "border-indigo-500 bg-white dark:bg-slate-900 ring-1 ring-indigo-300" : "border-slate-200 bg-white/60 dark:bg-slate-900/60 hover:border-indigo-300"}`}>
            <p className="text-sm font-medium text-slate-900 dark:text-white">{title}</p>
            <p className="text-[11px] text-slate-500">{hint}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

export function GroupBlock({ section, group, catCalc, readOnly, actions }) {
  const testBase = `qg-group-${group.id}`;
  const errorsFor = (iid) => catCalc.errors.filter((e) => e.item_id === iid);
  const groupTotal = group.items.reduce((s, it) => s + (catCalc.items[it.id]?.amount ?? 0n), 0n);
  return (
    <section className="space-y-3" data-testid={testBase}>
      <div className="flex items-center gap-3">
        <span className="h-px w-6 bg-brillien-500" />
        {group.code && <span className="font-mono text-xs text-brillien-600">{group.code}</span>}
        <Input value={group.title || ""} onChange={(e) => actions.setGroup(section.key, group.id, { title: e.target.value })} disabled={readOnly}
          placeholder="Add group heading (optional)" className="h-8 max-w-md border-transparent hover:border-slate-200 focus:border-slate-300 bg-transparent font-semibold uppercase tracking-wide text-sm text-slate-800 dark:text-slate-100 px-2 placeholder:normal-case placeholder:font-normal placeholder:tracking-normal placeholder:text-slate-300"
          data-testid={`${testBase}-title`} />
        <span className="ml-auto text-xs text-slate-500 tabular-nums" data-testid={`${testBase}-total`}>Group: {formatINR(groupTotal, true)}</span>
      </div>
      {group.has_package && (
        <PricingChoice group={group} readOnly={readOnly} testId={`${testBase}-pricing`} onChange={(v) => actions.setGroup(section.key, group.id, { pricing_choice: v })} />
      )}
      <div className="space-y-3">
        {group.items.map((it, i) => (
          <ItemCard key={it.id} section={section} group={group} item={it} calcItem={catCalc.items[it.id]} errors={errorsFor(it.id)}
            readOnly={readOnly} actions={actions} index={i} count={group.items.length} />
        ))}
      </div>
      {!readOnly && (
        <button type="button" onClick={() => actions.addItem(section.key, group.id)} data-testid={`${testBase}-add-item`}
          className="w-full rounded-xl border border-dashed border-slate-300 dark:border-slate-600 py-2.5 text-sm font-medium text-slate-500 hover:text-brillien-700 hover:border-brillien-400 transition-colors inline-flex items-center justify-center gap-1.5">
          <Plus className="h-4 w-4" />Add custom item{group.title ? ` to ${group.title}` : ""}
        </button>
      )}
    </section>
  );
}
